import { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Bell, BellOff, Loader2, Monitor, Play, Share, Smartphone } from 'lucide-react';
import { useI18n } from '../../contexts/I18nContext';
import { useAuthStore } from '../../store/useAuthStore';
import { Switch } from '../ui/Switch';
import { playSound, setSoundsEnabled, SOUND_NAMES, soundsEnabled } from '../../lib/sounds';
import { desktop } from '../../lib/desktop';
import {
  currentSubscription,
  disablePush,
  enablePush,
  PushError,
  pushPermission,
  pushSupport,
  type PushSupport,
} from '../../lib/push';
import {
  getPushConfig,
  getPushPreferences,
  sendTestPush,
  updatePushPreferences,
  type PushPreferences,
} from '../../lib/pushApi';

const CONFIG_KEY = ['push', 'config'] as const;
const PREFERENCES_KEY = ['push', 'preferences'] as const;

/**
 * Settings → Notificări push.
 *
 * The panel has to answer one question honestly before it offers anything:
 * can this browser receive notifications at all? On an iPhone the answer is no
 * until the app is installed to the Home Screen — Safari hides the Push API in
 * a normal tab — so that case gets instructions rather than a toggle that
 * would fail on click.
 *
 * The master switch is per BROWSER (it is a subscription this device holds),
 * while the three category switches are per PERSON and follow them to every
 * device. That split is deliberate and is spelled out on screen, because
 * "turned it off on my laptop" and "turned it off everywhere" are different
 * intentions.
 *
 * In the Firmio desktop app there is no browser subscription at all: the app
 * shows system notifications from the notification feed, so the panel says
 * they are on (or paused from the tray) and keeps the categories and the test.
 */
export function PushNotificationsSettings() {
  const { t } = useI18n();
  const qc = useQueryClient();
  const isAdmin = useAuthStore((s) => s.user?.role) === 'ADMIN';

  const [support, setSupport] = useState<PushSupport>('unsupported');
  const [subscribedHere, setSubscribedHere] = useState(false);
  const [checking, setChecking] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const config = useQuery({ queryKey: CONFIG_KEY, queryFn: getPushConfig });
  const preferences = useQuery({
    queryKey: PREFERENCES_KEY,
    queryFn: getPushPreferences,
    // The desktop app needs no push keys: its categories always apply.
    enabled: config.data?.enabled === true || support === 'desktop',
  });

  // Whether THIS browser holds a subscription is a browser question, not a
  // server one: the server knows how many devices exist, not which one is here.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const kind = pushSupport();
      const existing = kind === 'supported' ? await currentSubscription() : null;
      if (cancelled) return;
      setSupport(kind);
      setSubscribedHere(Boolean(existing) && pushPermission() === 'granted');
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const savePreferences = useMutation({
    mutationFn: (next: Partial<PushPreferences>) => updatePushPreferences(next),
    onSuccess: (saved) => qc.setQueryData(PREFERENCES_KEY, saved),
    onError: (e) => setError(e instanceof Error ? e.message : t('settings.unknownError')),
  });

  async function handleToggle(next: boolean) {
    setError(null);
    setNotice(null);
    setBusy(true);
    try {
      if (next) {
        const key = config.data?.publicKey;
        if (!key) throw new Error(t('push.errors.notConfigured'));
        await enablePush(key);
        setSubscribedHere(true);
        setNotice(t('push.enabledNotice'));
      } else {
        await disablePush();
        setSubscribedHere(false);
      }
      await qc.invalidateQueries({ queryKey: CONFIG_KEY });
    } catch (e) {
      // "Denied" is the one failure worth explaining at length: the browser
      // will not ask again, and the only way back is through its own settings.
      if (e instanceof PushError && e.reason === 'denied') {
        setError(t('push.errors.denied'));
      } else {
        setError(e instanceof Error ? e.message : t('settings.unknownError'));
      }
    } finally {
      setBusy(false);
    }
  }

  async function handleTest() {
    setError(null);
    setNotice(null);
    try {
      await sendTestPush();
      setNotice(t('push.testSent'));
    } catch (e) {
      setError(e instanceof Error ? e.message : t('settings.unknownError'));
    }
  }

  const categories = (
    <CategorySwitches
      prefs={preferences.data}
      loading={preferences.isLoading || !preferences.data}
      isAdmin={isAdmin}
      onChange={(next) => savePreferences.mutate(next)}
    />
  );
  const feedback = (
    <>
      {error && (
        <div className="mt-4 rounded-md border border-red-500/30 bg-red-500/10 p-3 text-sm text-red-600">
          {error}
        </div>
      )}
      {notice && (
        <div className="mt-4 rounded-md border border-emerald-500/30 bg-emerald-500/10 p-3 text-sm text-emerald-600">
          {notice}
        </div>
      )}
    </>
  );

  // The desktop app: no subscription to manage, whatever the server's keys.
  if (!checking && support === 'desktop') {
    return (
      <Panel>
        <DesktopStatus />
        {feedback}
        {categories}
        <SoundSettings />
        <TestButton enabled onTest={() => void handleTest()} />
      </Panel>
    );
  }

  // Push has no VAPID keys on this install: say so plainly rather than showing
  // a toggle that would 503.
  if (config.data && !config.data.enabled) {
    return (
      <Panel>
        <p className="text-sm text-gray-500">{t('push.notConfigured')}</p>
        <SoundSettings />
      </Panel>
    );
  }

  if (config.isLoading || checking) {
    return (
      <Panel>
        <p className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="h-4 w-4 animate-spin" /> {t('common.loading')}
        </p>
      </Panel>
    );
  }

  if (support === 'needs-install') {
    return (
      <Panel>
        <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
          <p className="flex items-center gap-2 text-sm font-medium text-amber-600">
            <Smartphone className="h-4 w-4" /> {t('push.iosTitle')}
          </p>
          <p className="mt-2 text-sm text-gray-500">{t('push.iosBody')}</p>
          <ol className="mt-3 space-y-1.5 text-sm text-gray-500">
            <li className="flex items-center gap-2">
              <Share className="h-4 w-4 shrink-0" /> {t('push.iosStep1')}
            </li>
            <li>{t('push.iosStep2')}</li>
            <li>{t('push.iosStep3')}</li>
          </ol>
        </div>
        <SoundSettings />
      </Panel>
    );
  }

  if (support === 'unsupported') {
    return (
      <Panel>
        <p className="text-sm text-gray-500">{t('push.unsupported')}</p>
        <SoundSettings />
      </Panel>
    );
  }

  const deviceCount = config.data?.devices.length ?? 0;

  return (
    <Panel>
      <button
        type="button"
        onClick={() => void handleToggle(!subscribedHere)}
        disabled={busy}
        className={`flex w-full items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-medium transition-colors disabled:opacity-60 ${
          subscribedHere
            ? 'border border-gray-300 text-gray-700 hover:bg-gray-100'
            : 'bg-brand-600 text-white hover:bg-brand-700'
        }`}
      >
        {busy ? (
          <Loader2 className="h-4 w-4 animate-spin" />
        ) : subscribedHere ? (
          <BellOff className="h-4 w-4" />
        ) : (
          <Bell className="h-4 w-4" />
        )}
        {subscribedHere ? t('push.disableHere') : t('push.enableHere')}
      </button>

      <p className="mt-2 text-center text-xs text-gray-500">
        {subscribedHere ? t('push.onHere') : t('push.offHere')}
        {deviceCount > 0 && ` · ${t('push.deviceCount', { count: deviceCount })}`}
      </p>

      {feedback}

      {/* The categories are per person, not per device, so they stay visible
          and editable even on a browser where notifications are switched off —
          otherwise someone would have to enable them on their laptop just to
          change what their phone is allowed to interrupt them about. */}
      {categories}

      <SoundSettings />

      {/* Always rendered, disabled with a reason when it cannot work — a test
          button that simply vanishes is the hardest kind to ask about. */}
      <TestButton enabled={subscribedHere} onTest={() => void handleTest()} />
    </Panel>
  );
}

/** The per-person category switches, the same for a browser and the desktop app. */
function CategorySwitches({
  prefs,
  loading,
  isAdmin,
  onChange,
}: {
  prefs: PushPreferences | undefined;
  loading: boolean;
  isAdmin: boolean;
  onChange: (next: Partial<PushPreferences>) => void;
}) {
  const { t } = useI18n();
  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-900">{t('push.categoriesTitle')}</h4>
      <p className="mt-1 text-xs text-gray-500">{t('push.categoriesHint')}</p>

      {loading || !prefs ? (
        <p className="mt-3 text-sm text-gray-500">{t('common.loading')}</p>
      ) : (
        <div className="mt-2 space-y-1">
          <Switch
            checked={prefs.tasks}
            onChange={(v) => onChange({ tasks: v })}
            label={t('push.category.tasks')}
            description={t('push.category.tasksHint')}
          />
          <Switch
            checked={prefs.chat}
            onChange={(v) => onChange({ chat: v })}
            label={t('push.category.chat')}
            description={t('push.category.chatHint')}
          />
          <Switch
            checked={prefs.voice}
            onChange={(v) => onChange({ voice: v })}
            label={t('push.category.voice')}
            description={t('push.category.voiceHint')}
          />
          <Switch
            checked={prefs.meetings}
            onChange={(v) => onChange({ meetings: v })}
            label={t('push.category.meetings')}
            description={t('push.category.meetingsHint')}
          />
          <Switch
            checked={prefs.leaves}
            onChange={(v) => onChange({ leaves: v })}
            label={t('push.category.leaves')}
            description={t('push.category.leavesHint')}
          />
          {isAdmin && (
            <Switch
              checked={prefs.alerts}
              onChange={(v) => onChange({ alerts: v })}
              label={t('push.category.alerts')}
              description={t('push.category.alertsHint')}
            />
          )}
        </div>
      )}
    </div>
  );
}

function TestButton({ enabled, onTest }: { enabled: boolean; onTest: () => void }) {
  const { t } = useI18n();
  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <button
        type="button"
        onClick={onTest}
        disabled={!enabled}
        className="rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
      >
        {t('push.sendTest')}
      </button>
      {!enabled && <p className="mt-2 text-xs text-gray-500">{t('push.sendTestNeedsEnable')}</p>}
    </div>
  );
}

/** The desktop app: notifications are on, or paused from the tray menu. */
function DesktopStatus() {
  const { t } = useI18n();
  const [enabled, setEnabled] = useState(true);

  useEffect(() => {
    const api = desktop();
    if (!api) return;
    let alive = true;
    void api.notificationsEnabled().then((on) => alive && setEnabled(on));
    const off = api.onNotificationsEnabledChange(setEnabled);
    return () => {
      alive = false;
      off();
    };
  }, []);

  return enabled ? (
    <div className="rounded-md border border-emerald-500/30 bg-emerald-500/10 p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-emerald-600">
        <Monitor className="h-4 w-4" /> {t('push.desktopActive')}
      </p>
      <p className="mt-1 text-sm text-gray-500">{t('push.desktopActiveHint')}</p>
    </div>
  ) : (
    <div className="rounded-md border border-amber-500/30 bg-amber-500/10 p-4">
      <p className="flex items-center gap-2 text-sm font-medium text-amber-600">
        <BellOff className="h-4 w-4" /> {t('push.desktopPaused')}
      </p>
    </div>
  );
}

/**
 * In-app sounds.
 *
 * Rendered in EVERY branch of the panel, including the ones that report push
 * as unusable: these play from the page itself and owe nothing to VAPID keys,
 * a service worker or an iPhone being installed to the Home Screen. Hiding
 * them behind push would have made them unreachable on exactly the
 * installations that need them most.
 */
function SoundSettings() {
  const { t } = useI18n();
  // Read once: the value lives in localStorage, not in React state.
  const [sounds, setSounds] = useState(() => soundsEnabled());

  return (
    <div className="mt-6 border-t border-gray-200 pt-4">
      <h4 className="text-sm font-semibold text-gray-900">{t('push.soundsTitle')}</h4>
      <p className="mt-1 text-xs text-gray-500">{t('push.soundsHint')}</p>

      <div className="mt-2">
        <Switch
          checked={sounds}
          onChange={(v) => {
            setSoundsEnabled(v);
            setSounds(v);
            if (v) playSound('message');
          }}
          label={t('push.soundsToggle')}
          description={t('push.soundsToggleHint')}
        />
      </div>

      <p className="mt-3 text-xs font-medium text-gray-500">{t('push.testSoundsLabel')}</p>
      <div className="mt-1.5 flex flex-wrap gap-2">
        {SOUND_NAMES.map((name) => (
          <button
            key={name}
            type="button"
            onClick={() => playSound(name)}
            disabled={!sounds}
            className="inline-flex items-center gap-1.5 rounded-md border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-100 disabled:opacity-50"
          >
            <Play className="h-3.5 w-3.5" />
            {t(`push.sound.${name}`)}
          </button>
        ))}
      </div>
    </div>
  );
}

function Panel({ children }: { children: React.ReactNode }) {
  const { t } = useI18n();
  return (
    <section className="max-w-2xl rounded-lg border border-gray-200 bg-card p-5">
      <div className="flex items-start gap-3">
        <div className="rounded-md bg-brand-600/15 p-2 text-brand-600">
          <Bell className="h-5 w-5" />
        </div>
        <div className="min-w-0">
          <h3 className="text-sm font-semibold text-gray-900">{t('push.title')}</h3>
          <p className="mt-1 text-sm text-gray-500">{t('push.description')}</p>
        </div>
      </div>
      <div className="mt-5">{children}</div>
    </section>
  );
}
